import FJAppConstant from '../constants/FJAppConstant';

/**
 * 用户选择/状态持久化存储
 * 专门用于在 LocalStorage 中保存用户在各个工具中选中的状态（如当前模型、比例等），
 * 并在页面刷新后恢复这些选择。
 */

// 定义存储的 Key 枚举
export const SELECTION_KEYS = {
    CUR_MODEL: 'CUR_MODEL',
    // 未来可以扩展其他状态，如 CUR_RATIO, CUR_RESOLUTION 等
};

class FJUserSelectionStore {
    constructor() {
        this.prefix = 'FJ_AI_SEL_'; // 前缀：FlexClip AI Selection
    }

    /**
     * 生成存储键名
     * @param {string} tool - 工具标识，来自 FJAppConstant
     * @param {string} key - 状态标识，来自 SELECTION_KEYS
     */
    _getStorageKey(tool, key) {
        if (!tool || !key) {
            console.warn('FJUserSelectionStore: tool or key is missing');
            return null;
        }
        // 确保 tool 是字符串
        const toolStr = String(tool).toUpperCase().replace(/-/g, '_');
        return `${this.prefix}${toolStr}_${key}`;
    }

    /**
     * 保存用户选择
     * @param {string} tool - 工具标识 (使用 FJAppConstant)
     * @param {string} key - 状态标识 (使用 SELECTION_KEYS)
     * @param {any} value - 要保存的值
     */
    save(tool, key, value) {
        try {
            const storageKey = this._getStorageKey(tool, key);
            if (!storageKey) return;

            const data = JSON.stringify(value);
            localStorage.setItem(storageKey, data);
        } catch (e) {
            console.warn('FJUserSelectionStore: Save failed', e);
        }
    }

    /**
     * 读取用户选择 (带校验)
     * @param {string} tool - 工具标识
     * @param {string} key - 状态标识
     * @param {any} defaultValue - 默认值 (当读取失败或校验失败时返回)
     * @param {Array|Function} [validator] - 校验器。
     *        - 如果是数组: 检查 value 是否存在于数组中 (数组项可以是值，也可以是包含 value 属性的对象)
     *        - 如果是函数: 调用 validator(value)，返回 true 表示有效
     */
    load(tool, key, defaultValue, validator) {
        try {
            const storageKey = this._getStorageKey(tool, key);
            if (!storageKey) return defaultValue;

            const raw = localStorage.getItem(storageKey);
            if (raw === null || raw === undefined) return defaultValue;

            const value = JSON.parse(raw);

            // 数据校验
            if (validator) {
                let isValid = false;
                if (Array.isArray(validator)) {
                    isValid = validator.some(item => {
                        // 兼容对象数组 (如 [{value: 'model1'}, ...]) 和 简单数组 (['model1', ...])
                        if (item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'value')) {
                            return item.value === value;
                        }
                        return item === value;
                    });
                } else if (typeof validator === 'function') {
                    isValid = validator(value);
                }

                if (!isValid) {
                    return defaultValue;
                }
            }

            return value;
        } catch (e) {
            console.warn('FJUserSelectionStore: Load failed', e);
            return defaultValue;
        }
    }
}

export default new FJUserSelectionStore();
